import React, { useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { TopNavigationBar } from '@common/components/navigation-bar';
import Container from '../../../../common/components/container';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import * as integrationFunctionsSelector from '../../selectors/integration-functions';
import * as integrationCommunitiesSelector from '../../selectors/integration-communities';
import {
  fetchIntegrationFunctions,
  syncIntegration,
  updateFunctionById,
} from '../../actions/integration-functions';
import {
  fetchIntegrationCommunities,
  updateNetworkById,
} from '../../actions/integration-communities';
import { fetchIntegrationUsers } from '../../actions/integration-users';
import type {
  Network,
  Organisation,
  OrganisationFunction,
} from '../../../../common/types/objects';
import type { StoreState } from '../../../../common/types/store';
import { SettingsCard } from './settings-card';
import { useForm } from 'react-hook-form';
import { SelectInputHookForm } from '@common/components/form/inputs/select';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import moment from 'moment';
import Spinner from '../../../../common/components/spinner';
import { ExternalUsersModal } from './external-users-modal';
import { ImportCommunitiesModal } from './import-communities-modal';
import { ImportFunctionsModal } from './import-functions-modal';
import { SyncResultModal } from './sync-result-modal';
import { Button } from '@common/components/button';
import { AlertService } from '@common/services/alert';
import { SyncResult, ExternalUsersResponse } from './types';
import { IntegrationSettingsModal } from './integration-settings-modal';
import { EPermissions } from '@common/definitions';
import { useCanAccess } from '@common/hooks';

const CustomValueComponent = ({ children, ...props }: any) => {
  const isUnmatched = props.data?.isUnmatched;

  return (
    <div
      {...props}
      className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-mr-1 tw-mb-1 ${
        isUnmatched
          ? 'tw-bg-orange-100 tw-text-orange-800 tw-border tw-border-orange-300'
          : 'tw-bg-blue-100 tw-text-blue-800 tw-border tw-border-blue-300'
      }`}
    >
      {children}
      <span
        className="tw-ml-1 tw-cursor-pointer tw-font-bold"
        onClick={(e) => {
          e.stopPropagation();
          props.removeProps?.onClick?.(e);
        }}
      >
        ×
      </span>
    </div>
  );
};

const getUnmatchedExternalIds = (
  externalIds: string | string[] | null | undefined,
  availableGroups: any[]
) => {
  if (!externalIds) return [];

  const idsArray = Array.isArray(externalIds) ? externalIds : [externalIds];

  const allGroupExternalIds: string[] = [];
  availableGroups.forEach((group) => {
    if (group.externalId) {
      if (Array.isArray(group.externalId)) {
        allGroupExternalIds.push(...group.externalId);
      } else {
        allGroupExternalIds.push(group.externalId);
      }
    }
  });

  return idsArray.filter((extId) => !allGroupExternalIds.some((groupId) => groupId === extId));
};

const createOptionsWithUnmatched = (
  externalId: string | string[] | null | undefined,
  availableGroups: any[]
) => {
  if (!externalId) return [];

  const idsArray = Array.isArray(externalId) ? externalId : [externalId];

  const matchingGroups = availableGroups.filter((group) => {
    if (!group.externalId) return false;

    if (Array.isArray(group.externalId)) {
      return idsArray.some((extId) => group.externalId.some((groupId: string) => groupId === extId)
      );
    }

    return idsArray.some((extId) => extId === group.externalId);
  });

  const unmatchedExternalIds = getUnmatchedExternalIds(
    externalId,
    availableGroups
  );

  const matchedOptions = matchingGroups.map((group) => ({
    value: group.externalId,
    label: group.name,
  }));

  const unmatchedOptions = unmatchedExternalIds.map((extId) => ({
    value: extId,
    label: extId,
    isUnmatched: true,
  }));

  return [...matchedOptions, ...unmatchedOptions];
};


type Props = {
  t: (key: string) => string;
  organisation: Organisation;
  functionGroups: any[];
  communityGroups: any[];
  isSyncing: boolean;
  functions: OrganisationFunction[];
  networks: Network[];
  fetchIntegrationFunction: (organisationId: string) => Promise<any>;
  fetchIntegrationCommunitie: (organisationId: string) => Promise<any>;
  fetchIntegrationUser: (organisationId: string) => Promise<any>;
  syncIntegrations: (organisationId: string, drySync?: boolean) => Promise<SyncResult>;
  updateFunction: (
    organisationId: string,
    functionId: string,
    functionData: { externalId: string[], name?: string }
  ) => Promise<any>;
  updateNetwork: (
    networkId: string,
    networkData: { externalId: string[], name?: string }
  ) => Promise<any>;
};

export const OrganisationIntegrationSettings = ({
  t,
  organisation,
  functionGroups,
  communityGroups,
  isSyncing,
  functions,
  networks,
  fetchIntegrationFunction,
  fetchIntegrationCommunitie,
  fetchIntegrationUser,
  syncIntegrations,
  updateFunction,
  updateNetwork,
}: Props) => {
  const [communityDisplayCount, setCommunityDisplayCount] = useState(5);
  const [functionDisplayCount, setFunctionDisplayCount] = useState(5);
  const [loadingMoreCommunities, setLoadingMoreCommunities] = useState(false);
  const [loadingMoreFunctions, setLoadingMoreFunctions] = useState(false);
  const [isExternalUsersModalOpen, setIsExternalUsersModalOpen] =
    useState(false);
  const [isImportCommunitiesModalOpen, setIsImportCommunitiesModalOpen] =
    useState(false);
  const [isImportFunctionsModalOpen, setIsImportFunctionsModalOpen] =
    useState(false);
  const [isSyncResultModalOpen, setIsSyncResultModalOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialFunctionValues, setInitialFunctionValues] = useState<any>({});
  const [initialCommunityValues, setInitialCommunityValues] = useState<any>({});
  const [isConfirmSyncing, setIsConfirmSyncing] = useState(false);
  const [isRefreshingFunctions, setIsRefreshingFunctions] = useState(false);
  const [isRefreshingCommunities, setIsRefreshingCommunities] = useState(false);
  const [externalUsers, setExternalUsers] = useState<ExternalUsersResponse | null>(null);
  const [isLoadingExternalUsers, setIsLoadingExternalUsers] = useState(false);
  const [isIntegrationSettingsModalOpen, setIsIntegrationSettingsModalOpen] = useState(false);
  const functionLengthRef = useRef(functions.length);
  const networkLengthRef = useRef(networks.length);
  const isOrganisationAdmin = useCanAccess(
    {
      permissions: EPermissions.ORGANISATION_ORGANISATION_ADMINS_VIEW,
    }
  );

  useEffect(() => {
    if (organisation?.id) {
      const loadIntegrationData = async () => {
        setIsRefreshingFunctions(true);
        setIsRefreshingCommunities(true);

        try {
          await Promise.all([
            fetchIntegrationFunction(organisation.id),
            fetchIntegrationCommunitie(organisation.id),
          ]);
        } catch (error) {
          AlertService.error(t('organisation:integration_settings_load_error'));
        } finally {
          setIsRefreshingFunctions(false);
          setIsRefreshingCommunities(false);
        }
      };

      loadIntegrationData();
    }
  }, [
    organisation?.id,
    fetchIntegrationFunction,
    fetchIntegrationCommunitie,
  ]);

  const getFunctionDefaultValues = () => {
    const defaultValues: any = {};

    functions.forEach((func, index) => {
      const linkedOptions = createOptionsWithUnmatched(func.external_id, functionGroups);

      defaultValues[`function_network_name_${index}`] = func.name;
      defaultValues[`function_linked_with_${index}`] = linkedOptions;
    });

    return defaultValues;
  };

  const getCommunityDefaultValues = () => {
    const defaultValues: any = {};

    networks.forEach((network, index) => {
      const linkedOptions = createOptionsWithUnmatched(network.external_id, communityGroups);

      defaultValues[`network_name_${index}`] = network.name;
      defaultValues[`linked_with_${index}`] = linkedOptions;
    });

    return defaultValues;
  };


  const {
    control: functionControl,
    reset: resetFunctionForm,
    watch: watchFunctionForm,
  } = useForm({
    defaultValues: getFunctionDefaultValues(),
    mode: 'all',
  });

  const {
    control: communityControl,
    reset: resetCommunityForm,
    watch: watchCommunityForm,
  } = useForm({
    defaultValues: getCommunityDefaultValues(),
    mode: 'all',
  });

  const functionFormValues = watchFunctionForm();
  const communityFormValues = watchCommunityForm();

  useEffect(() => {
    const checkForChanges = () => {
      const functionChanged =
        JSON.stringify(functionFormValues) !==
        JSON.stringify(initialFunctionValues);
      const communityChanged =
        JSON.stringify(communityFormValues) !==
        JSON.stringify(initialCommunityValues);
      setHasChanges(functionChanged || communityChanged);
    };

    checkForChanges();
  }, [
    functionFormValues,
    communityFormValues,
    initialFunctionValues,
    initialCommunityValues,
  ]);

  useEffect(() => {
    const newFunctionValues = getFunctionDefaultValues();
    resetFunctionForm(newFunctionValues);
    setInitialFunctionValues(newFunctionValues);
  }, [functionGroups, resetFunctionForm, functions]);

  useEffect(() => {
    const newCommunityValues = getCommunityDefaultValues();
    resetCommunityForm(newCommunityValues);
    setInitialCommunityValues(newCommunityValues);
  }, [communityGroups, resetCommunityForm, networks]);

  useEffect(() => {
    if (functions.length > functionLengthRef.current) {
      const addedCount = functions.length - functionLengthRef.current;
      setFunctionDisplayCount((prev) => Math.min(functions.length, prev + addedCount)
      );
    } else if (functions.length < functionLengthRef.current) {
      setFunctionDisplayCount((prev) => Math.min(prev, functions.length));
    }
    functionLengthRef.current = functions.length;
  }, [functions.length]);

  useEffect(() => {
    if (networks.length > networkLengthRef.current) {
      const addedCount = networks.length - networkLengthRef.current;
      setCommunityDisplayCount((prev) => Math.min(networks.length, prev + addedCount)
      );
    } else if (networks.length < networkLengthRef.current) {
      setCommunityDisplayCount((prev) => Math.min(prev, networks.length));
    }
    networkLengthRef.current = networks.length;
  }, [networks.length]);

  const transformToOption = (fn: { externalId: string; name: string }) => ({
    value: fn.externalId,
    label: fn.name,
  });

  const handleLoadMoreCommunities = () => {
    setLoadingMoreCommunities(true);
    setCommunityDisplayCount(networks.length);
    setLoadingMoreCommunities(false);
  };

  const handleLoadMoreFunctions = () => {
    setLoadingMoreFunctions(true);
    setFunctionDisplayCount(functions.length);
    setLoadingMoreFunctions(false);
  };

  const handleOpenExternalUsersModal = async () => {
    setIsExternalUsersModalOpen(true);
    setIsLoadingExternalUsers(true);
    if (organisation?.id) {
      try {
        const response = await fetchIntegrationUser(organisation.id);
        setExternalUsers(response);
      } catch (error) {
        AlertService.error(t('organisation:integration_users_load_error'));
      } finally {
        setIsLoadingExternalUsers(false);
      }
    } else {
      setIsLoadingExternalUsers(false);
    }
  };

  const handleForceSync = async () => {
    if (!organisation?.id) return;
    try {
      const result = await syncIntegrations(organisation.id, true);
      setSyncResult(result);
      AlertService.success(t('organisation:integration_settings_force_sync_success'));
      setIsSyncResultModalOpen(true);
    } catch (error) {
      AlertService.error(t('organisation:integration_settings_force_sync_failed'));
    }
  };

  const handleConfirmSync = async () => {
    if (!organisation?.id) return;
    setIsConfirmSyncing(true);
    try {
      await syncIntegrations(organisation.id, false);
      AlertService.success(t('organisation:integration_settings_force_sync_success'));
      setIsSyncResultModalOpen(false);

      setIsRefreshingFunctions(true);
      setIsRefreshingCommunities(true);
      await Promise.all([
        fetchIntegrationFunction(organisation.id),
        fetchIntegrationCommunitie(organisation.id),
        fetchIntegrationUser(organisation.id)
      ]);
    } catch (error) {
      AlertService.error(t('organisation:integration_settings_force_sync_failed'));
    } finally {
      setIsConfirmSyncing(false);
      setIsRefreshingFunctions(false);
      setIsRefreshingCommunities(false);
    }
  };


  const handleSave = async () => {
    if (!organisation?.id || !hasChanges) return;

    setIsSaving(true);
    try {
      const promises: Promise<any>[] = [];

      Object.keys(functionFormValues)
        .filter((key) => key.startsWith('function_network_name_'))
        .forEach((key) => {
          const index = key.replace('function_network_name_', '');
          const functionItem = functions[parseInt(index)];

          if (!functionItem) return;

          const nameKey = `function_network_name_${index}`;
          const linkedWithKey = `function_linked_with_${index}`;

          const currentName = functionFormValues[nameKey];
          const initialName = initialFunctionValues[nameKey];
          const linkedWith = functionFormValues[linkedWithKey] || [];
          const initialLinkedWith = initialFunctionValues[linkedWithKey] || [];

          const currentExternalIds = linkedWith
            .map((item: any) => item.value)
            .flat();
          const initialExternalIds = initialLinkedWith
            .map((item: any) => item.value)
            .flat();

          const hasExternalIdChanged =
            JSON.stringify(currentExternalIds.sort()) !==
            JSON.stringify(initialExternalIds.sort());
          const hasNameChanged = currentName !== initialName;

          if (hasExternalIdChanged || hasNameChanged) {
            const functionData: { externalId: string[], name?: string } = {
              externalId: currentExternalIds,
            };

            if (hasNameChanged && currentName) {
              functionData.name = currentName;
            }

            promises.push(
              updateFunction(organisation.id, functionItem.id, functionData)
            );
          }
        });

      Object.keys(communityFormValues)
        .filter((key) => key.startsWith('network_name_'))
        .forEach((key) => {
          const index = key.replace('network_name_', '');
          const networkItem = networks[parseInt(index)];

          if (!networkItem) return;

          const nameKey = `network_name_${index}`;
          const linkedWithKey = `linked_with_${index}`;

          const currentName = communityFormValues[nameKey];
          const initialName = initialCommunityValues[nameKey];
          const linkedWith = communityFormValues[linkedWithKey] || [];
          const initialLinkedWith = initialCommunityValues[linkedWithKey] || [];

          const currentExternalIds = linkedWith
            .map((item: any) => item.value)
            .flat();
          const initialExternalIds = initialLinkedWith
            .map((item: any) => item.value)
            .flat();

          const hasExternalIdChanged =
            JSON.stringify(currentExternalIds.sort()) !==
            JSON.stringify(initialExternalIds.sort());
          const hasNameChanged = currentName !== initialName;

          if (hasExternalIdChanged || hasNameChanged) {
            const networkData: { externalId: string[], name?: string } = {
              externalId: currentExternalIds,
            };

            if (hasNameChanged && currentName) {
              networkData.name = currentName;
            }

            promises.push(updateNetwork(networkItem.id, networkData));
          }
        });

      if (promises.length > 0) {
        await Promise.all(promises);
        AlertService.success(t('organisation:integration_settings_save_success'));
      }


      setInitialFunctionValues(functionFormValues);
      setInitialCommunityValues(communityFormValues);
      setHasChanges(false);
    } catch (error) {
      AlertService.error(t('organisation:integration_settings_save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const displayedNetworks = networks.slice(0, communityDisplayCount);
  const displayedFunction = functions.slice(0, functionDisplayCount);
  const hasMoreCommunities = networks.length > communityDisplayCount;
  const hasMoreFunctions = functions.length > functionDisplayCount;

  const handleOpenIntegrationSettingsModal = () => {
    setIsIntegrationSettingsModalOpen(true);
  };

  return (
    <Container name="SettingsIntegration">
      <TopNavigationBar
        title={[t('organisation:settings_index_general_title')]}
        action={
          <Button
            type="primary"
            size="large"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            isLoading={isSaving}
          >
            {t('organisation:integration_settings_save_button')}
          </Button>
        }
      />
      <Container.Content className="tw-flex tw-flex-col tw-gap-6">
        <SettingsCard
          title={t('organisation:integration_settings_general_title')}
          description={t(
            'organisation:integration_settings_general_description'
          )}
          content={
            <>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-flex tw-flex-row tw-gap-1">
                  <div className="tw-w-[165px] tw-text-base tw-font-medium tw-font-inter tw-text-[#71717A]">
                    {t('organisation:integration_settings_type_label')}
                  </div>
                  <div className="tw-text-base tw-font-medium tw-font-inter tw-text-[#3F3F46]">
                    {organisation &&
                      organisation.integrations &&
                      organisation.integrations.length > 0
                      ? organisation.integrations[0].integration_type
                      : ''}
                  </div>
                </div>
                <div className="tw-flex tw-flex-row tw-gap-1">
                  <div className="tw-w-[165px] tw-text-base tw-font-medium tw-font-inter tw-text-[#71717A]">
                    {t('organisation:integration_settings_last_synced_label')}
                  </div>
                  <div className="tw-text-base tw-font-medium tw-font-inter tw-text-[#3F3F46]">
                    {organisation &&
                      moment(organisation.last_synced_at).format(
                        'MMMM D, YYYY HH:mm'
                      )}
                  </div>
                </div>
              </div>
              <div className="tw-h-[1px] tw-bg-gray-200" />
            </>
          }
          footer={
            <>
              <Button
                onClick={handleForceSync}
                disabled={isSyncing}
                isLoading={isSyncing}
                type="primary"
              >
                {t('organisation:integration_settings_force_sync_button')}
              </Button>
              <Button onClick={handleOpenExternalUsersModal}>
                {t(
                  'organisation:integration_settings_view_external_users_button'
                )}
              </Button>
              <Button onClick={handleOpenIntegrationSettingsModal}>
                {t('common:settings')}
              </Button>
            </>
          }
        />

        <SettingsCard
          title={t('organisation:integration_settings_community_mapping_title')}
          description={t(
            'organisation:integration_settings_community_mapping_description'
          )}
          action={(
            <Button
              type="primary"
              icon="download"
              onClick={() => setIsImportCommunitiesModalOpen(true)}
            >
              {t('organisation:integration_settings_import_button')}
            </Button>
          )}
          content={
            <div className="tw-flex tw-flex-col">
              <div className="tw-flex tw-flex-row tw-font-semibold tw-text-xs tw-text-gray-500 tw-mb-[6px]">
                <div className="tw-w-1/2 tw-font-inter tw-font-normal tw-text-gray-text tw-text-[13px]">
                  {t(
                    'organisation:integration_settings_oneteam_community_label'
                  )}
                </div>
                <div className="tw-w-1/2 tw-pl-4 tw-font-inter tw-font-normal tw-text-gray-text tw-text-[13px]">
                  {t('organisation:integration_settings_linked_with_label')}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-4">
                {isRefreshingCommunities ? (
                  <div className="tw-flex tw-justify-center tw-items-center tw-h-16 tw-text-gray-500">
                    <Spinner centered />
                  </div>
                ) : networks.length === 0 ? (
                  <div className="tw-flex tw-justify-center tw-items-center tw-h-16 tw-text-gray-500">
                    <p className="tw-text-sm tw-text-gray-500">
                      {t('organisation:integration_settings_no_communities_found')}
                    </p>
                  </div>
                ) : (
                  <>
                    {displayedNetworks.map((network, index) => (
                      <div
                        key={network.id}
                        className="tw-flex tw-flex-row tw-gap-4"
                      >
                        <div className="tw-w-1/2">
                          <TextInputHookForm
                            name={`network_name_${index}`}
                            control={communityControl}
                            disabled={!isOrganisationAdmin}
                            placeholder={t(
                              'organisation:integration_settings_community_name_placeholder'
                            )}
                          />
                        </div>
                        <div className="tw-w-1/2">
                          <SelectInputHookForm
                            name={`linked_with_${index}`}
                            control={communityControl}
                            searchable
                            multi
                            options={communityGroups.map(transformToOption)}
                            valueComponent={CustomValueComponent}
                          />
                        </div>
                      </div>
                    ))}

                    {hasMoreCommunities && (
                      <div
                        className="tw-flex tw-justify-center tw-items-center tw-py-2 tw-font-medium tw-text-gray-500
                        tw-cursor-pointer tw-hover:text-gray-700 tw-transition-colors"
                        onClick={
                          !loadingMoreCommunities
                            ? handleLoadMoreCommunities
                            : undefined
                        }
                        role="button"
                      >
                        {t('common:table_show_more')}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          }
        />

        <SettingsCard
          title={t('organisation:integration_settings_function_mapping_title')}
          description={t(
            'organisation:integration_settings_function_mapping_description'
          )}
          action={[
            <Button
              type="primary"
              icon="download"
              onClick={() => setIsImportFunctionsModalOpen(true)}
            >
              {t('organisation:integration_settings_import_button')}
            </Button>,
          ]}
          content={
            <div className="tw-flex tw-flex-col">
              <div className="tw-flex tw-flex-row tw-font-semibold tw-text-xs tw-text-gray-500 tw-mb-[6px]">
                <div className="tw-w-1/2 tw-font-inter tw-font-normal tw-text-gray-text tw-text-[13px]">
                  {t(
                    'organisation:integration_settings_oneteam_function_group_label'
                  )}
                </div>
                <div className="tw-w-1/2 tw-pl-4 tw-font-inter tw-font-normal tw-text-gray-text tw-text-[13px]">
                  {t('organisation:integration_settings_linked_with_label')}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-4">
                {isRefreshingFunctions ? (
                  <div className="tw-flex tw-justify-center tw-items-center tw-h-16 tw-text-gray-500">
                    <Spinner centered />
                  </div>
                ) : functions.length === 0 ? (
                  <div className="tw-flex tw-justify-center tw-items-center tw-h-16 tw-text-gray-500">
                    <p className="tw-text-sm tw-text-gray-500">
                      {t('organisation:integration_settings_no_functions_found')}
                    </p>
                  </div>
                ) : (
                  <>
                    {displayedFunction.map((func, index) => (
                      <div
                        key={func.id}
                        className="tw-flex tw-flex-row tw-gap-4"
                      >
                        <div className="tw-w-1/2">
                          <TextInputHookForm
                            name={`function_network_name_${index}`}
                            disabled={!isOrganisationAdmin}
                            control={functionControl}
                            placeholder={t(
                              'organisation:integration_settings_function_group_name_placeholder'
                            )}
                          />
                        </div>
                        <div className="tw-w-1/2">
                          <SelectInputHookForm
                            name={`function_linked_with_${index}`}
                            control={functionControl}
                            searchable
                            multi
                            options={functionGroups.map(transformToOption)}
                            valueComponent={CustomValueComponent}
                          />
                        </div>
                      </div>
                    ))}
                    {hasMoreFunctions && (
                      <div className="tw-flex tw-justify-center tw-items-center tw-py-4">
                        <div
                          className="tw-flex tw-justify-center tw-items-center tw-py-2 tw-px-4 tw-font-medium tw-text-gray-500
                          tw-cursor-pointer tw-hover:text-gray-700 tw-transition-colors"
                          onClick={
                            !loadingMoreFunctions
                              ? handleLoadMoreFunctions
                              : undefined
                          }
                          role="button"
                        >
                          {t('common:table_show_more')}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          }
        />
      </Container.Content>

      <ExternalUsersModal
        isOpen={isExternalUsersModalOpen}
        externalUsers={externalUsers}
        isLoading={isLoadingExternalUsers}
        onClose={() => setIsExternalUsersModalOpen(false)}
      />

      <ImportCommunitiesModal
        isOpen={isImportCommunitiesModalOpen}
        onClose={() => setIsImportCommunitiesModalOpen(false)}
        organisationId={organisation?.id || ''}
      />

      <ImportFunctionsModal
        isOpen={isImportFunctionsModalOpen}
        onClose={() => setIsImportFunctionsModalOpen(false)}
        organisationId={organisation?.id || ''}
      />

      <SyncResultModal
        isOpen={isSyncResultModalOpen}
        onClose={() => setIsSyncResultModalOpen(false)}
        syncData={syncResult}
        onConfirmSync={handleConfirmSync}
        isLoading={isConfirmSyncing}
      />

      <IntegrationSettingsModal
        isOpen={isIntegrationSettingsModalOpen}
        onClose={() => setIsIntegrationSettingsModalOpen(false)}
        organisationId={organisation?.id || ''}
      />

    </Container>
  );
};

const mapStateToProps = (state: StoreState) => ({
  organisation: organisationSelector.selected(state),
  functionGroups: integrationFunctionsSelector.getIntegrationFunctions(state),
  functions: organisationSelector.functions(state),
  networks: organisationSelector.networks(state),
  communityGroups:
    integrationCommunitiesSelector.getIntegrationCommunities(state),
  isSyncing: integrationFunctionsSelector.getIntegrationSyncing(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchIntegrationFunction: (organisationId: string) => dispatch(fetchIntegrationFunctions(organisationId)),
  fetchIntegrationCommunitie: (organisationId: string) => dispatch(fetchIntegrationCommunities(organisationId)),
  fetchIntegrationUser: (organisationId: string) => dispatch(fetchIntegrationUsers(organisationId)),
  syncIntegrations: (organisationId: string, drySync?: boolean) => dispatch(syncIntegration(organisationId, drySync)),
  updateFunction: (
    organisationId: string,
    functionId: string,
    functionData: { externalId: string[] }
  ) => dispatch(updateFunctionById(organisationId, functionId, functionData)),
  updateNetwork: (
    networkId: string,
    networkData: { externalId: string[] }
  ) => dispatch(updateNetworkById(networkId, networkData)),
});

const mapper = connect(mapStateToProps, mapDispatchToProps);

export const OrganisationIntegrationSettingsContainer = withTranslation()(
  mapper(OrganisationIntegrationSettings)
);
